// src/features/contractor/schema/contractorSchema.ts
import * as Yup from "yup";
import dayjs from "dayjs";
import { buildStringField } from "@utils/validationHelpers";
import { TFunction } from "i18next";

type Translator = (key: string, vars?: Record<string, any>) => string;

export default function createContractorSchema(t: TFunction | Translator) {
  const today = new Date();

  return Yup.object().shape({
    firstName: buildStringField({
      label: t("CreateNewContractor.firstName"),
      regex: /^[A-Za-z\s-]+$/,
      regexMsg: t("CreateNewContractor.validations.name", {
        value: t("CreateNewContractor.firstName"),
      }),
      min: 3,
      minMsg: t("CreateNewContractor.validations.min", {
        value: t("CreateNewContractor.firstName"),
        min: "3",
      }),
      max: 255,
      maxMsg: t("CreateNewContractor.validations.max", {
        value: t("CreateNewContractor.firstName"),
        amount: "255",
      }),
    }),

    secondFirstName: buildStringField({
      label: t("CreateNewContractor.secondFirstName"),
      required: false,
      regex: /^[A-Za-z\s-]+$/,
      regexMsg: t("CreateNewContractor.validations.name", {
        value: t("CreateNewContractor.secondFirstName"),
      }),
      min: 3,
      minMsg: t("CreateNewContractor.validations.min", {
        value: t("CreateNewContractor.secondFirstName"),
        min: "3",
      }),
      max: 255,
      maxMsg: t("CreateNewContractor.validations.max", {
        value: t("CreateNewContractor.secondFirstName"),
        amount: "255",
      }),
    }),

    lastName: buildStringField({
      label: t("CreateNewContractor.lastName"),
      regex: /^[A-Za-z\s-]+$/,
      regexMsg: t("CreateNewContractor.validations.name", {
        value: t("CreateNewContractor.lastName"),
      }),
      min: 3,
      minMsg: t("CreateNewContractor.validations.min", {
        value: t("CreateNewContractor.lastName"),
        min: "3",
      }),
      max: 255,
      maxMsg: t("CreateNewContractor.validations.max", {
        value: t("CreateNewContractor.lastName"),
        amount: "255",
      }),
    }),

    secondLastName: buildStringField({
      label: t("CreateNewContractor.secondLastName"),
      required: false,
      regex: /^[A-Za-z\s-]+$/,
      regexMsg: t("CreateNewContractor.validations.name", {
        value: t("CreateNewContractor.secondLastName"),
      }),
      min: 3,
      minMsg: t("CreateNewContractor.validations.min", {
        value: t("CreateNewContractor.secondLastName"),
        min: "3",
      }),
      max: 255,
      maxMsg: t("CreateNewContractor.validations.max", {
        value: t("CreateNewContractor.secondLastName"),
        amount: "255",
      }),
    }),

    streetAddress: buildStringField({
      label: t("CreateNewContractor.streetAddress"),
      regex: /^[A-Za-z0-9\s]+$/,
      regexMsg: t("CreateNewContractor.validations.alphaNum", {
        value: t("CreateNewContractor.streetAddress"),
      }),
      min: 3,
      minMsg: t("CreateNewContractor.validations.min", {
        value: t("CreateNewContractor.streetAddress"),
        min: "3",
      }),
      max: 255,
      maxMsg: t("CreateNewContractor.validations.max", {
        value: t("CreateNewContractor.streetAddress"),
        amount: "255",
      }),
    }),

    city: buildStringField({
      label: t("CreateNewContractor.city"),
      regex: /^[A-Za-z\s]+$/,
      regexMsg: t("CreateNewContractor.validations.alpha", {
        value: t("CreateNewContractor.city"),
      }),
      min: 3,
      minMsg: t("CreateNewContractor.validations.min", {
        value: t("CreateNewContractor.city"),
        min: "3",
      }),
      max: 255,
      maxMsg: t("CreateNewContractor.validations.max", {
        value: t("CreateNewContractor.city"),
        amount: "255",
      }),
    }),

    zipCode: buildStringField({
      label: t("CreateNewContractor.zipCode"),
      regex: /^[0-9]+$/,
      regexMsg: t("CreateNewContractor.validations.num", {
        value: t("CreateNewContractor.zipCode"),
      }),
      length: 5,
      lengthMsg: t("CreateNewContractor.validations.length", {
        value: t("CreateNewContractor.zipCode"),
        len: "5",
        units: "digits",
      }),
    }),

    state: buildStringField({
      label: t("CreateNewContractor.state"),
      regex: /^[A-Za-z\s]+$/,
      regexMsg: t("CreateNewContractor.validations.alpha", {
        value: t("CreateNewContractor.state"),
      }),
    }),

    country: buildStringField({
      label: t("CreateNewContractor.country"),
      regex: /^[A-Za-z\s]+$/,
      regexMsg: t("CreateNewContractor.validations.alpha", {
        value: t("CreateNewContractor.country"),
      }),
    }),

    birthDate: Yup.date()
      .nullable()
      .required(
        t("CreateNewContractor.validations.required", {
          value: t("CreateNewContractor.birthdate"),
        })
      )
      .min(
        new Date(1900, 1, 1),
        t("CreateNewContractor.validations.minDate", {
          value: t("CreateNewContractor.birthdate"),
        })
      )
      .max(
        dayjs(today).add(1, "day").toDate(),
        t("CreateNewContractor.validations.maxDate", {
          value: t("CreateNewContractor.birthdate"),
        })
      ),

    preferredLang: buildStringField({
      label: t("CreateNewContractor.preferredLang"),
      regex: /^[A-Za-z\s]+$/,
      regexMsg: t("CreateNewContractor.validations.alpha", {
        value: t("CreateNewContractor.preferredLang"),
      }),
    }),

    email: Yup.string()
      .required(
        t("CreateNewContractor.validations.required", {
          value: t("CreateNewContractor.email"),
        })
      )
      .email(t("CreateNewContractor.validations.email")),

    phone: buildStringField({
      label: t("CreateNewContractor.phoneNum"),
      regex: /^[0-9]+$/,
      regexMsg: t("CreateNewContractor.validations.num", {
        value: t("CreateNewContractor.phoneNum"),
      }),
      length: 10,
      lengthMsg: t("CreateNewContractor.validations.length", {
        value: t("CreateNewContractor.phoneNum"),
        len: "10",
        units: "digits",
      }),
    }),

    plant: buildStringField({ label: t("CreateNewContractor.plant") }),

    facility: Yup.string(),

    contractorId: buildStringField({
      label: t("CreateNewContractor.contractorId"),
      regex: /^[0-9]+$/,
      regexMsg: t("CreateNewContractor.validations.num", {
        value: t("CreateNewContractor.contractorId"),
      }),
    }),

    firstWorkingDay: Yup.date()
      .nullable()
      .required(
        t("CreateNewContractor.validations.required", {
          value: t("CreateNewContractor.firstWorkDay"),
        })
      ),

    driversLicense: buildStringField({
      label: t("CreateNewContractor.driversLicense"),
    }),

    payType: buildStringField({
      label: t("CreateNewContractor.payType"),
      regex: /^[A-Za-z\s]+$/,
      regexMsg: t("CreateNewContractor.validations.alpha", {
        value: t("CreateNewContractor.payType"),
      }),
    }),

    bankAccountNumber: buildStringField({
      label: t("CreateNewContractor.bankAccount"),
      required: false,
      regex: /^[0-9]+$/,
      regexMsg: t("CreateNewContractor.validations.num", {
        value: t("CreateNewContractor.bankAccount"),
      }),
      max: 18,
      maxMsg: t("CreateNewContractor.validations.max", {
        value: t("CreateNewContractor.bankAccount"),
        amount: "18",
      }),
    }),

    employeeType: buildStringField({
      label: t("CreateNewContractor.employeeType"),
    }),

    payRateClass: buildStringField({
      label: t("CreateNewContractor.payRateClass"),
    }),

    payRate: buildStringField({
      label: t("CreateNewContractor.payRate"),
      regex: /^[0-9.]+$/,
      regexMsg: t("CreateNewContractor.validations.decimalNum", {
        value: t("CreateNewContractor.payRate"),
      }),
    }),

    billingRateClass: buildStringField({
      label: t("CreateNewContractor.billingRateClass"),
    }),

    billingRate: buildStringField({
      label: t("CreateNewContractor.billingRate"),
      regex: /^[0-9.]+$/,
      regexMsg: t("CreateNewContractor.validations.decimalNum", {
        value: t("CreateNewContractor.billingRate"),
      }),
    }),

    taxWithHolding: buildStringField({
      label: t("CreateNewContractor.taxWithholding"),
      regex: /^[0-9.]+$/,
      regexMsg: t("CreateNewContractor.validations.decimalNum", {
        value: t("CreateNewContractor.taxWithholding"),
      }),
    }),

    rentDeduction: buildStringField({
      label: t("CreateNewContractor.rent"),
      required: false,
      regex: /^[0-9.]+$/,
      regexMsg: t("CreateNewContractor.validations.decimalNum", {
        value: t("CreateNewContractor.rent"),
      }),
    }),

    adminDeduction: buildStringField({
      label: t("CreateNewContractor.admin"),
      required: false,
      regex: /^[0-9.]+$/,
      regexMsg: t("CreateNewContractor.validations.decimalNum", {
        value: t("CreateNewContractor.admin"),
      }),
    }),

    visaBondDeduction: buildStringField({
      label: t("CreateNewContractor.visaBond"),
      required: false,
      regex: /^[0-9.]+$/,
      regexMsg: t("CreateNewContractor.validations.decimalNum", {
        value: t("CreateNewContractor.visaBond"),
      }),
    }),

    miscDeduction: buildStringField({
      label: t("CreateNewContractor.misc"),
      required: false,
      regex: /^[0-9.]+$/,
      regexMsg: t("CreateNewContractor.validations.decimalNum", {
        value: t("CreateNewContractor.misc"),
      }),
    }),
  });
}
